import axios from 'axios';
import { includes, get } from 'lodash';

import history from '../history';
import { getItem, removeItem, setItem } from './localStorageService';
import { store } from '../store';
import { authSetAuthToken, signOutUser } from '../actions/auth';

export const LOCAL_URL =
  process.env.API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://www.localhost.com:1337';

const redirectToAuth = () => {
  removeItem('token');
  removeItem('isLoggedIn');
  setItem('refreshTokenFound', 'false');
  const authStatus = get(store.getState(), 'auth.authStatus');
  // Reducer only sets authStatus to 'signout' when SIGN_OUT_USER is
  // dispatched. history.push alone does not update Redux.
  if (authStatus !== 'signout') {
    store.dispatch(signOutUser());
    return;
  }
  history.push('/auth');
};

const isTokenExpired = response => {
  const message =
    get(response, 'data.message') ||
    get(response, 'message') ||
    get(response, 'data');
  return message === 'token_expired';
};

export const request = {
  init() {
    const req = axios.create({
      withCredentials: true,
      baseURL: LOCAL_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.attachInterceptor(req);
    return req;
  },

  attachInterceptor(req) {
    req.interceptors.response.use(
      response => {
        if (isTokenExpired(response)) {
          redirectToAuth();
          return null;
        }
        return response;
      },
      error => {
        const { response, config } = error;
        const status = response?.status || error.request?.status;

        // 403 + token_expired hits the error interceptor (not success).
        if (status === 403 && isTokenExpired(response)) {
          redirectToAuth();
          return Promise.reject(error);
        }

        if (status === 401) {
          if (
            !getItem('refreshTokenFound') ||
            getItem('refreshTokenFound') === 'false'
          ) {
            redirectToAuth();
            return Promise.reject(error);
          }
          const refreshInstance = axios.create({
            withCredentials: true,
            baseURL: LOCAL_URL,
            headers: {
              'Content-Type': 'application/json',
            },
          });
          return refreshInstance.get('/auth/token/refresh').then(({ data }) => {
            if (data.error) {
              redirectToAuth();
              return Promise.reject(error);
            }
            setItem('refreshTokenFound', 'true');
            store.dispatch(authSetAuthToken(data.token));
            const newConfig = {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `bearer ${data.token}`,
              },
            };
            return req(newConfig);
          });
        }
        const handledErrors = [404, 403];
        if (includes(handledErrors, status)) {
          history.push(`/${status}`);
        }
        // eslint-disable-next-line consistent-return
        return Promise.reject(error);
      }
    );
    req.interceptors.request.use(config => {
      const token = get(store.getState(), 'auth.authToken');
      if (!token) {
        const refreshInstance = axios.create({
          withCredentials: true,
          baseURL: LOCAL_URL,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return refreshInstance.get('/auth/token/refresh').then(({ data }) => {
          if (!data.error) {
            store.dispatch(authSetAuthToken(data.token));
            setItem('refreshTokenFound', 'true');
            return {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `bearer ${data.token}`,
              },
            };
          }
          setItem('refreshTokenFound', 'false');
          return config;
        });
      }
      return {
        ...config,
        headers: { ...config.headers, Authorization: `bearer ${token}` },
      };
    });
  },

  get(...args) {
    return this.init().get.apply(null, args);
  },

  post(...args) {
    return this.init().post.apply(null, args);
  },

  delete(...args) {
    return this.init().delete.apply(null, args);
  },

  put(...args) {
    return this.init().put.apply(null, args);
  },
};
